import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import NetworkManager from '../../api/NetworkManager';
import { getStoresLastModified } from '../../api/audit';
import { useAuthContext } from '../../context/AuthContext';
import type { Store } from '../../context/StoreManagementContext';
import { STALE_TIME_STORES } from '../../constants';

export const STORES_QUERY_KEY = ['stores'] as const;

// Default page size for pagination
const PAGE_SIZE = 50;

interface StoresResponse {
  status: boolean;
  data: any[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Transform raw store data to Store interface
 */
function transformStore(store: any): Store {
  return {
    storeNumber: store.storeNumber,
    storeName: store.name,
    name: store.name, // Add name field for mobile views
    brand: store.brand,
    franchiseGroup: store.franchiseGroup,
    timezone: store.timezone,
    address: {
      street: store.address?.street,
      city: store.address?.city,
      state: store.address?.state,
      zip: store.address?.zip,
    },
    // Use acceptingOnlineOrders from Byte GraphQL
    onlineOrders: store.acceptingOnlineOrders ?? false,
    // Include all ByteConnect channels from backend
    channels: store.channels || store.activeChannels || [],
    // Include ByteConnect channels from backend (deprecated)
    activeChannels: store.activeChannels || [],
    // Keep legacy aggregators for backward compatibility
    deliveryAggregators: store.deliveryAggregators || [],
    status: store.status || 'active',
    isActive: store.status === 'active' || store.isActive !== false,
    unavailableItems: store.unavailableItems || [],
    // Include business hours from Byte Graph
    hours: store.businessHours || store.hours || [],
  };
}

/**
 * TanStack Query hook for fetching and caching store data with pagination.
 *
 * Uses useInfiniteQuery internally but auto-fetches all pages for backward
 * compatibility with components that expect all stores at once.
 *
 * Returns:
 * - data: Flattened array of all stores (for backward compatibility)
 * - isLoading: True while fetching any page
 * - error: Error if any page fails
 * - fetchNextPage: Function to manually load next page
 * - hasNextPage: Whether more pages exist
 * - isFetchingNextPage: True while fetching additional pages
 * - totalStores: Total number of stores available
 */
export function useStoresQuery() {
  const { accessToken } = useAuthContext();

  const infiniteQuery = useInfiniteQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: async ({
      pageParam = 0,
    }): Promise<{
      stores: Store[];
      pagination: StoresResponse['pagination'];
    }> => {
      // Fetch stores from backend with pagination
      const response = await NetworkManager.get<StoresResponse>(
        `/user/stores?includeHours=true&limit=${PAGE_SIZE}&offset=${pageParam}`
      );
      const storesData = response.data || [];
      const pagination = response.pagination || {
        total: storesData.length,
        limit: PAGE_SIZE,
        offset: pageParam,
        hasMore: false,
      };

      // Transform to Store interface
      const transformedStores: Store[] = storesData.map(transformStore);

      // Fetch last modified info for this page's stores
      if (transformedStores.length > 0) {
        const storeNumbers = transformedStores.map((s) => s.storeNumber);
        try {
          const lastModifiedData = await getStoresLastModified(storeNumbers);
          transformedStores.forEach((store) => {
            const lastMod = lastModifiedData[store.storeNumber];
            if (lastMod) {
              store.lastModified = lastMod;
            }
          });
        } catch (lastModErr) {
          // Don't fail the whole load if last modified fails - just log it
          console.warn('Failed to fetch last modified data:', lastModErr);
        }
      }

      return { stores: transformedStores, pagination };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasMore) {
        return lastPage.pagination.offset + lastPage.pagination.limit;
      }
      return undefined;
    },
    enabled: !!accessToken,
    staleTime: STALE_TIME_STORES,
  });

  // Auto-fetch all remaining pages for backward compatibility
  // This ensures components that expect all stores work without changes
  useEffect(() => {
    if (
      infiniteQuery.hasNextPage &&
      !infiniteQuery.isFetchingNextPage &&
      !infiniteQuery.isLoading
    ) {
      infiniteQuery.fetchNextPage();
    }
  }, [
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.isLoading,
    infiniteQuery.fetchNextPage,
  ]);

  // Flatten all pages into a single array for backward compatibility
  const data = useMemo(() => {
    if (!infiniteQuery.data?.pages) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.stores);
  }, [infiniteQuery.data?.pages]);

  // Get total stores from the first page's pagination info
  const totalStores = infiniteQuery.data?.pages?.[0]?.pagination?.total ?? 0;

  // Determine if we're still loading (initial or fetching more pages)
  const isLoading = infiniteQuery.isLoading || infiniteQuery.isFetchingNextPage;

  return {
    // Backward-compatible flat array of stores
    data,
    // Standard query states
    isLoading,
    error: infiniteQuery.error,
    isError: infiniteQuery.isError,
    // Pagination-specific (for components that want manual control)
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    totalStores,
    // Refetch function
    refetch: infiniteQuery.refetch,
  };
}

/**
 * Hook to get a function that invalidates the stores query.
 * Use this to trigger a refetch after mutations.
 */
export function useInvalidateStores() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
}
